import { useCallback, useEffect, useState } from 'react';
import { auditsAPI, type Audit, type AuditListParams } from '../../lib/api';
import { apiError } from './useAuditActions';

/**
 * Audits visible to the caller, re-fetched when the filters change.
 *
 * @param params - supplier / status / auditor filters
 * @param enabled - false while Audit Lite is off (nothing is fetched)
 * @returns rows, loading and error flags, and `reload`
 * @example const { audits, loading } = useAudits({ supplier: 12 });
 */
export function useAudits(params: AuditListParams, enabled: boolean = true) {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);
  const key = JSON.stringify(params);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      setAudits(await auditsAPI.list(JSON.parse(key) as AuditListParams));
    } catch (err: unknown) {
      console.error('Failed to load audits:', err);
      setError(apiError(err, 'Could not load audits.'));
    } finally {
      setLoading(false);
    }
  }, [key, enabled]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { audits, loading, error, reload };
}
